/**
 * Plan de la base temps reel.
 *
 * La Realtime Database n'a pas de requetes croisees : on ne peut pas
 * demander "les posts que j'ai mis en signet". La regle est donc de
 * ranger la donnee du cote ou on la lit, quitte a la dupliquer.
 *
 * Chaque chemin est expose ici sous forme de fonction : aucune chaine
 * de chemin n'est ecrite ailleurs dans le code, ce qui rend une
 * reorganisation de la base indolore.
 *
 *   servers/{serverId}                         catalogue des villes
 *   users/{uid}                                profil complet
 *   usernames/{pseudoEnMinuscules}             unicite et recherche
 *
 *   posts/{serverId}/{postId}                  feed d'une ville
 *   postIndex/{postId}                         { serverId, authorId }
 *   postLikes/{postId}/{uid}                   qui a like quoi
 *   comments/{postId}/{commentId}
 *   commentLikes/{commentId}/{uid}             qui a like quel commentaire
 *   lieuLikes/{lieuId}/{uid}                   qui a like quel lieu d'Apple
 *   bookmarkedPosts/{uid}/{postId}             signets de posts
 *   bookmarkedComments/{uid}/{commentId}       signets de commentaires
 *   hiddenPosts/{uid}/{postId}                 posts masques
 *
 *   events/{serverId}/{eventId}
 *   eventIndex/{eventId}                       { serverId, organizerId }
 *   eventParticipants/{eventId}/{uid}          "pending" ou "confirmed"
 *   bookmarkedEvents/{uid}/{eventId}
 *   hiddenEvents/{uid}/{eventId}               evenements masques
 *
 *   trophies/{serverId}/{trophyId}             catalogue des trophees
 *   unlockedTrophies/{uid}/{serverId}/{id}     ceux qu'un membre a
 *   scores/{spot}/{periode}/{uid}              points, periode = all | 2026-08
 *   journalPoints/{uid}/{spot}/{id}            ce qui a rapporte, et quand
 *   stats/{uid}/{spot}                         compteurs des trophees
 *
 *   conversations/{conversationId}
 *   userConversations/{uid}/{conversationId}   mes conversations
 *   messages/{conversationId}/{messageId}
 *   lastRead/{conversationId}/{uid}            horodatage de lecture
 *   users/{uid}/nonLus/{conversationId}        compteur tenu par le serveur
 *
 *   friends/{uid}/{autreUid}
 *   friendRequests/{uid}/{requestId}           demandes recues
 *   sentRequests/{uid}/{autreUid}              demandes envoyees
 *   friendRequestSent/{uid}/{autreUid}         derniere demande annoncee (fonctions seules)
 *   blockedUsers/{uid}/{autreUid}              membres que j'ai bloques
 *   hiddenUsers/{uid}/{autreUid}               comptes que j'ai masques
 *   conversationsFermees/{convId}/{uid}        tete-a-tete ferme a un membre bloque
 *   directConversations/{uidA_uidB}            le tete-a-tete de deux membres
 *   coPresence/{conversationId}                fin de la fenetre de 7 jours
 *   eventAttendance/{eventId}/{uid}            presence constatee
 *   eventSheets/{eventId}                      feuille de presence arretee
 *   organizerRatings/{orgId}/{eventId}/{uid}   la note d'un participant
 *   absences/{uid}/{eventId}                   une absence constatee
 *   reports/{reportId}                         signalements
 *
 *
 *   deviceTokens/{uid}/{token}                 ou joindre un membre
 *   voipTokens/{uid}/{token}                   ou le faire sonner, pour un appel
 *
 *   calls/{callId}                             un appel et sa negociation
 *   callCandidates/{callId}/{role}             les chemins reseau proposes
 *   incomingCalls/{uid}/{callId}               ce qui sonne chez moi
 */

export const paths = {
  servers: () => 'servers',
  server: (serverId: string) => `servers/${serverId}`,
  /** La cle d'un serveur prive : la ligne d'un membre sur sa liste. */
  serveurPriveMembre: (serverId: string, uid: string) => `serveursPrives/${serverId}/${uid}`,
  /** Toute la liste d'un serveur prive : ses membres la lisent pour l'annuaire. */
  serveurPriveListe: (serverId: string) => `serveursPrives/${serverId}`,
  /** Les formations des membres d'une ecole, et celle d'un membre. */
  parcours: (serverId: string) => `parcours/${serverId}`,
  parcoursDe: (serverId: string, uid: string) => `parcours/${serverId}/${uid}`,
  /** Qui appartient aux associations d'une ecole : `{ asso: { uid: 'chef' | 'membre' } }`. */
  associationMembres: (serverId: string) => `associationMembres/${serverId}`,
  associationMembresDe: (serverId: string, asso: string) => `associationMembres/${serverId}/${asso}`,
  associationMembre: (serverId: string, asso: string, uid: string) =>
    `associationMembres/${serverId}/${asso}/${uid}`,
  /** Les demandes pour entrer dans une association : son chef les lit. */
  associationDemandes: (serverId: string, asso: string) => `associationDemandes/${serverId}/${asso}`,
  associationDemande: (serverId: string, asso: string, uid: string) =>
    `associationDemandes/${serverId}/${asso}/${uid}`,

  users: () => 'users',
  user: (uid: string) => `users/${uid}`,
  userField: (uid: string, field: string) => `users/${uid}/${field}`,
  usernames: () => 'usernames',
  username: (username: string) => `usernames/${normalizeUsername(username)}`,

  posts: (serverId: string) => `posts/${serverId}`,
  /**
   * Index d'un post : ou il vit, et qui l'a ecrit.
   * Il evite de balayer tous les serveurs pour retrouver un post, et
   * permet aux regles de securite de reconnaitre son auteur quand elles
   * n'ont que son identifiant sous la main (suppression des likes et des
   * commentaires).
   */
  postIndex: (postId: string) => `postIndex/${postId}`,
  post: (serverId: string, postId: string) => `posts/${serverId}/${postId}`,
  postLikes: (postId: string) => `postLikes/${postId}`,
  postLike: (postId: string, uid: string) => `postLikes/${postId}/${uid}`,
  comments: (postId: string) => `comments/${postId}`,
  comment: (postId: string, commentId: string) => `comments/${postId}/${commentId}`,
  /*
    Les likes d'un commentaire vivent a part, comme ceux d'un post : les
    regles reservent l'ecriture d'un commentaire a son auteur, donc
    personne d'autre ne pourrait y incrementer un compteur.
  */
  commentLikes: (commentId: string) => `commentLikes/${commentId}`,
  commentLike: (commentId: string, uid: string) => `commentLikes/${commentId}/${uid}`,
  /*
    Un restaurant ne depend pas d'un serveur : deux villes voisines voient le
    meme, avec les memes likes.
  */
  lieuLikes: (lieuId: string) => `lieuLikes/${lieuId}`,
  lieuLike: (lieuId: string, uid: string) => `lieuLikes/${lieuId}/${uid}`,
  bookmarkedPosts: (uid: string) => `bookmarkedPosts/${uid}`,
  bookmarkedPost: (uid: string, postId: string) => `bookmarkedPosts/${uid}/${postId}`,
  bookmarkedComments: (uid: string) => `bookmarkedComments/${uid}`,
  bookmarkedComment: (uid: string, commentId: string) => `bookmarkedComments/${uid}/${commentId}`,
  hiddenPosts: (uid: string) => `hiddenPosts/${uid}`,
  hiddenPost: (uid: string, postId: string) => `hiddenPosts/${uid}/${postId}`,

  events: (serverId: string) => `events/${serverId}`,
  event: (serverId: string, eventId: string) => `events/${serverId}/${eventId}`,
  /**
   * Index d'un evenement, jumeau de `postIndex` : ou il vit, et qui
   * l'organise. Les regles n'ont que l'identifiant de l'evenement sous la
   * main quand l'organisateur valide un participant — sans cet index,
   * elles ne pourraient pas le reconnaitre.
   */
  eventIndex: (eventId: string) => `eventIndex/${eventId}`,
  eventParticipants: (eventId: string) => `eventParticipants/${eventId}`,
  eventParticipant: (eventId: string, uid: string) => `eventParticipants/${eventId}/${uid}`,
  /**
   * Quand chacun a demande a venir.
   *
   * `eventParticipants` ne retient qu'un mot — « pending » ou
   * « confirmed » — et les cles de Firebase sortent dans l'ordre
   * alphabetique : rien ne disait qui avait demande en premier. Or c'est
   * exactement ce qu'une liste d'attente doit savoir. On range donc la
   * date a cote, plutot que de changer la forme de l'autre branche et
   * tout ce qui la lit.
   */
  eventRequests: (eventId: string) => `eventRequests/${eventId}`,
  eventRequest: (eventId: string, uid: string) => `eventRequests/${eventId}/${uid}`,
  /* Un depart volontaire, le temps que la fonction previenne l'organisateur. */
  eventDeparture: (eventId: string, uid: string) => `eventDepartures/${eventId}/${uid}`,
  eventRemoval: (eventId: string, uid: string) => `eventRemovals/${eventId}/${uid}`,
  /*
    Qui s'est presente, par opposition a qui s'etait inscrit. La presence
    vit a part du statut parce qu'elle n'appartient pas au participant :
    les regles la reservent en ecriture a l'organisateur, alors que
    chacun ecrit son propre statut. Melangees, un inscrit se pointerait
    present tout seul.
  */
  eventAttendance: (eventId: string) => `eventAttendance/${eventId}`,
  eventAttendee: (eventId: string, uid: string) => `eventAttendance/${eventId}/${uid}`,
  /*
    La feuille de presence arretee.

    Tant qu'elle ne l'est pas, une absence n'existe pas : un organisateur
    qui oublie de pointer ne doit penaliser personne. C'est ce geste-la,
    et lui seul, qui transforme "pas de marque de presence" en absence.
  */
  eventSheets: () => 'eventSheets',
  eventSheet: (eventId: string) => `eventSheets/${eventId}`,

  /*
    Les notes recues par un organisateur, rangees de son cote : c'est la
    qu'on les lit — sur son profil, et sur la fiche de ses evenements.
    Une par evenement et par participant.
  */
  organizerRatings: (organizerId: string) => `organizerRatings/${organizerId}`,
  organizerRatingsOfEvent: (organizerId: string, eventId: string) =>
    `organizerRatings/${organizerId}/${eventId}`,
  organizerRating: (organizerId: string, eventId: string, raterId: string) =>
    `organizerRatings/${organizerId}/${eventId}/${raterId}`,

  /*
    Les absences d'un membre. Ecrites par l'organisateur qui arrete sa
    feuille, lisibles par le seul interesse : personne n'a a consulter le
    casier de quelqu'un d'autre.
  */
  absences: (uid: string) => `absences/${uid}`,
  absence: (uid: string, eventId: string) => `absences/${uid}/${eventId}`,

  bookmarkedEvents: (uid: string) => `bookmarkedEvents/${uid}`,
  bookmarkedEvent: (uid: string, eventId: string) => `bookmarkedEvents/${uid}/${eventId}`,
  hiddenEvents: (uid: string) => `hiddenEvents/${uid}`,
  hiddenEvent: (uid: string, eventId: string) => `hiddenEvents/${uid}/${eventId}`,

  trophies: (serverId: string) => `trophies/${serverId}`,
  trophy: (serverId: string, trophyId: string) => `trophies/${serverId}/${trophyId}`,
  unlockedTrophies: (uid: string, serverId: string) => `unlockedTrophies/${uid}/${serverId}`,
  /**
   * Quand j'ai regarde mes trophees pour la derniere fois.
   *
   * Le seul moyen de dire « celui-la est neuf » : la date de deblocage
   * vit dans `unlockedTrophies`, mais rien ne disait si on l'avait vue.
   */
  trophiesVu: (uid: string) => `trophiesVu/${uid}`,
  /**
   * Les lieux d'un trophee deja vus, par membre et par ville.
   *
   * Ecrit par la seule fonction qui juge les passages : un trophee de
   * lieu qu'on pourrait se donner ne vaudrait rien.
   */
  visitedPlaces: (uid: string, serverId: string) => `visitedPlaces/${uid}/${serverId}`,
  /** Les metres deja marches pour un trophee de distance. */
  walked: (uid: string, serverId: string) => `walked/${uid}/${serverId}`,
  /*
    Le classement. Deux ecritures a l'attribution — la periode courante et
    « toujours » — plutot qu'une agregation a la lecture : un classement
    se lit cent fois pour une ecriture.
  */
  scores: (serverId: string, periode: string) => `scores/${serverId}/${periode}`,
  scoreOf: (serverId: string, periode: string, uid: string) =>
    `scores/${serverId}/${periode}/${uid}`,
  journalPoints: (uid: string, serverId: string) => `journalPoints/${uid}/${serverId}`,
  stats: (uid: string, serverId: string) => `stats/${uid}/${serverId}`,

  leaderboard: (serverId: string) => `leaderboard/${serverId}`,
  leaderboardEntry: (serverId: string, uid: string) => `leaderboard/${serverId}/${uid}`,

  conversations: () => 'conversations',
  conversation: (conversationId: string) => `conversations/${conversationId}`,
  conversationMembers: (conversationId: string) => `conversations/${conversationId}/members`,
  conversationMember: (conversationId: string, uid: string) =>
    `conversations/${conversationId}/members/${uid}`,
  /*
    Le tete-a-tete de deux membres, retrouve par leur paire.

    Sans lui, on cherchait une conversation existante parmi les siennes —
    et l'on n'en trouvait plus des qu'on l'avait retiree de sa liste. Il
    s'en creait alors une seconde, si bien que l'autre se retrouvait avec
    deux fils pour la meme personne. La cle est faite des deux
    identifiants tries : elle ne depend pas de qui la calcule.
  */
  /*
    La fenetre de sept jours qui suit une rencontre. Elle vit ici, et non
    sur la conversation : la regle laisse chacun ecrire dans les
    conversations dont il est membre, et une regle enfant ne peut pas
    retirer un droit accorde au parent — posee la, elle serait forgeable.
  */
  /* Qui j'ai croise dans cette ville, atteste par une presence constatee. */
  rencontres: (serverId: string, uid: string) => `rencontres/${serverId}/${uid}`,

  coPresence: (conversationId: string) => `coPresence/${conversationId}`,

  directConversation: (a: string, b: string) => `directConversations/${[a, b].sort().join('_')}`,
  userConversations: (uid: string) => `userConversations/${uid}`,
  userConversation: (uid: string, conversationId: string) =>
    `userConversations/${uid}/${conversationId}`,
  messages: (conversationId: string) => `messages/${conversationId}`,
  message: (conversationId: string, messageId: string) => `messages/${conversationId}/${messageId}`,
  /*
    Les messages non lus, comptes par la Cloud Function qui recoit chaque
    message. La pastille de la barre d'onglets ne lit plus que cette
    branche : avant, elle relisait toutes les conversations a chaque
    message recu, meme depuis un autre onglet.
  */
  unread: (uid: string) => `users/${uid}/nonLus`,
  unreadOf: (uid: string, conversationId: string) => `users/${uid}/nonLus/${conversationId}`,

  lastRead: (conversationId: string, uid: string) => `lastRead/${conversationId}/${uid}`,
  /* Toute la branche : les accuses de lecture d'un fil se suivent d'un bloc. */
  lastReadOf: (conversationId: string) => `lastRead/${conversationId}`,

  /**
   * « Il écrit… ».
   *
   * Une marque horodatee, effacee a l'envoi et par `onDisconnect` si
   * l'application meurt en cours de frappe. Elle ne survit pas non plus a
   * son age : une marque trop vieille ne compte plus, ce qui rattrape le
   * telephone qui perd le reseau sans se deconnecter.
   */
  // Les emojis poses sur un message, ranges par celui qui les pose.
  reaction: (conversationId: string, messageId: string, uid: string) =>
    `reactions/${conversationId}/${messageId}/${uid}`,
  reactionsOf: (conversationId: string) => `reactions/${conversationId}`,

  typing: (conversationId: string, uid: string) => `typing/${conversationId}/${uid}`,
  typingOf: (conversationId: string) => `typing/${conversationId}`,

  friends: (uid: string) => `friends/${uid}`,
  friend: (uid: string, otherUid: string) => `friends/${uid}/${otherUid}`,
  /**
   * Le repere qui dit qu'une demande vient d'etre acceptee.
   *
   * Accepter ecrit l'amitie des deux cotes a la fois : un declencheur
   * pose sur `friends` previendrait donc aussi celui qui vient d'appuyer
   * sur « accepter ». Ce repere-la ne s'ecrit qu'une fois, et il nomme
   * exactement celui qui a quelque chose a apprendre. La fonction qui
   * l'entend l'efface.
   */
  friendAccepted: (uid: string, parQui: string) => `friendAccepted/${uid}/${parQui}`,
  friendRequests: (uid: string) => `friendRequests/${uid}`,
  friendRequest: (uid: string, requestId: string) => `friendRequests/${uid}/${requestId}`,
  sentRequests: (uid: string) => `sentRequests/${uid}`,
  sentRequest: (uid: string, otherUid: string) => `sentRequests/${uid}/${otherUid}`,
  blockedUsers: (uid: string) => `blockedUsers/${uid}`,
  blockedUser: (uid: string, otherUid: string) => `blockedUsers/${uid}/${otherUid}`,
  /** Le tete-a-tete ferme a `uid` par l'autre membre, qui l'a bloque. */
  conversationFermee: (conversationId: string, uid: string) =>
    `conversationsFermees/${conversationId}/${uid}`,
  hiddenUsers: (uid: string) => `hiddenUsers/${uid}`,
  hiddenUser: (uid: string, otherUid: string) => `hiddenUsers/${uid}/${otherUid}`,

  reports: () => 'reports',

  deviceTokens: (uid: string) => `deviceTokens/${uid}`,
  deviceToken: (uid: string, token: string) => `deviceTokens/${uid}/${token}`,

  /**
   * Ou joindre un membre **pour un appel**.
   *
   * Une branche a part : le jeton VoIP est delivre par PushKit, l'autre
   * par les notifications ordinaires, et les deux ne s'echangent pas.
   * Les melanger ne produirait aucune erreur, seulement un silence.
   */
  /**
   * Ce qui est arrive a ce que j'ai publie.
   *
   * Ecrit par les fonctions, lu par son seul proprietaire. La cloche du
   * fil s'en sert : contrairement aux notifications, qui passent et
   * s'oublient, ce registre garde — il dit ce qui s'est produit pendant
   * qu'on n'etait pas la.
   */
  activite: (uid: string) => `activite/${uid}`,
  /** Quand j'ai ouvert la cloche pour la derniere fois. */
  activiteVue: (uid: string) => `activiteVue/${uid}`,

  voipTokens: (uid: string) => `voipTokens/${uid}`,
  voipToken: (uid: string, token: string) => `voipTokens/${uid}/${token}`,

  calls: () => 'calls',
  call: (callId: string) => `calls/${callId}`,
  callField: (callId: string, field: string) => `calls/${callId}/${field}`,
  /** Les chemins reseau proposes, deposes de chaque cote. */
  callCandidates: (callId: string, role: 'caller' | 'callee') => `callCandidates/${callId}/${role}`,
  /** Camera coupee, de chaque cote : le flux seul ne le dit pas. */
  callCamera: (callId: string, role: 'caller' | 'callee') => `calls/${callId}/camera/${role}`,
  incomingCalls: (uid: string) => `incomingCalls/${uid}`,
  incomingCall: (uid: string, callId: string) => `incomingCalls/${uid}/${callId}`,

  /* La derniere version publiee sur l'App Store ou le Play Store, et la
     demande de la revoir posee sous un compte. */
  versionPubliee: (plateforme: 'ios' | 'android') => `app/versions/${plateforme}`,

  /* Description, logo et couleur des associations d'une ecole, a part de
     la fiche du serveur que lit chaque ville. */
  associationsFiches: (serverId: string) => `associationsFiches/${serverId}`,
  associationFiche: (serverId: string, asso: string) => `associationsFiches/${serverId}/${asso}`,
  /* Le groupe de chaque association, que tient la fonction `tenirLeGroupeDeLAsso`. */
  associationGroupe: (serverId: string, asso: string) => `associationGroupes/${serverId}/${asso}`,
  annonceAVoir: (uid: string) => `users/${uid}/voirLAnnonce`,

  /* Releves temporaires d'un appareil : la demande, et ce qui a ete note. */
  sondeDemandee: (uid: string, nom: string) => `diagnostics/${uid}/${nom}/actif`,
  sondeReleves: (uid: string, nom: string) => `diagnostics/${uid}/${nom}/releves`,
} as const;

/**
 * Cle de pseudo : minuscules et sans accents.
 * Sert d'index d'unicite, donc "Léa_27" et "lea_27" doivent tomber sur
 * la meme entree.
 */
export function normalizeUsername(username: string): string {
  return (
    username
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Les caracteres interdits dans une cle Firebase.
      .replace(/[.#$/[\]]/g, '_')
  );
}
